import React, { useState, useEffect, useCallback } from "react";
import Skeleton from "../common/Skeleton";
import { Plus, Search, CheckCircle, Save } from "lucide-react";
import Modal from "../common/Modal";
import Button from "../common/Button";
import FormControl from "../common/FormControl";
import Pagination from "../common/Pagination";
import Toast, { ToastMessage } from "../common/Toast";
import { confirmDelete } from "../common/confirm";
import NoDataFound from "../common/NoDataFound";
import { severityBadgeCss } from "../../utils/statusVariant";
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";
import {
  controlDeficiencyService,
  ControlDeficiency,
  DeficiencySeverity,
  DeficiencyStatus,
  DeficiencySource,
  DEFICIENCY_SEVERITIES,
  DEFICIENCY_STATUSES,
  DEFICIENCY_SOURCES,
} from "../../services/controlDeficiencyService";
import apiClient from "../../services/apiService";
import extractApiError from "../../utils/apiError";

interface DeficiencyForm {
  title: string;
  controlId: number | "";
  source: string;
  severity: string;
  description: string;
}

const BLANK_FORM: DeficiencyForm = {
  title: "",
  controlId: "",
  source: "",
  severity: "",
  description: "",
};

const SOURCE_OPTIONS = DEFICIENCY_SOURCES.map((s) => ({
  value: s,
  label: s.replace("_", "-").replace(/\b\w/g, (c) => c.toUpperCase()),
}));
const SEVERITY_OPTIONS = DEFICIENCY_SEVERITIES.map((s) => ({ value: s, label: s }));

const SourceBadge = ({ source }: { source: DeficiencySource }) => (
  <span className="status-badge sm info capitalize">{source.replace("_", "-")}</span>
);

// Match CAT Programs (ControlPrograms.tsx) — surface deficiency status using the
// same three CAT stage labels/colours: In-Progress (blue) / Submitted (purple) /
// Closed (green). DB values stay Open/In-Progress/Resolved/Closed; this is
// display-only (deficiencies aren't maker/checker so there's no migration).
const DEFICIENCY_STAGE_LABELS: Record<string, string> = {
  Open: "In-Progress",
  "In-Progress": "In-Progress",
  Resolved: "Submitted",
  Closed: "Closed",
};
const deficiencyStageLabel = (status?: string | null) =>
  DEFICIENCY_STAGE_LABELS[status || ""] || status || "In-Progress";
const deficiencyStageClass = (status?: string | null) => {
  switch (deficiencyStageLabel(status)) {
    case "Closed":      return "bg-green-100 text-green-700";
    case "Submitted":   return "bg-purple-100 text-purple-700";
    case "In-Progress": return "bg-blue-100 text-blue-700";
    default:            return "bg-gray-100 text-gray-600";
  }
};

const ControlDeficiencies: React.FC = () => {
  const { allowed: canView, loading: permViewLoading } = usePermission('CTR_DEFICIENCIES', 'view');
  const { allowed: canModify, loading: permLoading } = usePermission('CTR_DEFICIENCIES', 'view+modify');
  const { formatDate } = useEntitySettings();

  const [deficiencies, setDeficiencies] = useState<ControlDeficiency[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [severityFilter, setSeverityFilter] = useState<string>("All");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState<DeficiencyForm>(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [controlOptions, setControlOptions] = useState<Array<{ value: number; label: string }>>([]);
  const vh = useFieldValidation();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data, total } = await controlDeficiencyService.listAll({
        source: undefined,
        status: statusFilter !== "All" ? (statusFilter as DeficiencyStatus) : undefined,
        severity: severityFilter !== "All" ? (severityFilter as DeficiencySeverity) : undefined,
        search: searchTerm || undefined,
        page,
        limit: pageSize,
      });
      setDeficiencies(data);
      setTotal(total);
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Failed to load deficiencies") });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, searchTerm, page, pageSize]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, severityFilter, pageSize]);

  useEffect(() => {
    if (!showCreateModal) { setSubmitting(false); vh.reset(); return; }
    // Lazy-load the approved+active controls for the Create picker.
    (async () => {
      try {
        const r = await apiClient.get("/api/v1/ctr/controls/dropdown");
        const items = (r.data?.items ?? []) as Array<{ id: number; name: string; code?: string }>;
        setControlOptions(items.map((c) => ({ value: c.id, label: c.code ? `${c.name} (${c.code})` : c.name })));
      } catch (e) {
        setToast({ type: "error", message: extractApiError(e, "Failed to load controls") });
      }
    })();
  }, [showCreateModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = vh.validateAll([
      { name: 'title',       code: 109, value: form.title,       required: true },
      { name: 'description', code: 110, value: form.description, required: true },
    ]);
    if (form.controlId === "" || !form.source || !form.severity || !valid) {
      if (form.controlId === "") setToast({ type: "error", message: "Please select a control" });
      return;
    }
    setSubmitting(true);
    try {
      await controlDeficiencyService.create(Number(form.controlId), {
        title: form.title,
        description: form.description,
        source: form.source as DeficiencySource,
        detected_severity: form.severity as DeficiencySeverity,
      });
      setShowCreateModal(false);
      setForm(BLANK_FORM);
      setToast({ type: "success", message: "Deficiency created successfully." });
      setPage(1);
      await refresh();
    } catch (err: any) {
      setToast({ type: "error", message: extractApiError(err, "Create failed") });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async (item: ControlDeficiency) => {
    const ok = await confirmDelete({
      title: "Close Deficiency?",
      text: `Verify remediation is complete before closing "${item.title || item.description}".`,
      confirmText: "Yes, Close",
    });
    if (!ok) return;
    try {
      await controlDeficiencyService.update(item.id, { status: "Closed" });
      setToast({ type: "success", message: "Deficiency closed." });
      await refresh();
    } catch (e: any) {
      setToast({ type: "error", message: extractApiError(e, "Failed to close") });
    }
  };

  // ── Not-authorised gate ─────────────────────────────────────────────────
  if (!permViewLoading && !canView) {
    return (
      <NoDataFound title="Not authorised" message="You do not have access to 'CTR_DEFICIENCIES'." />
    );
  }

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="netbody-card flex flex-col h-full">
        <div className="netbody-header">
          <div className="flex items-center gap-2 ml-auto">
            <FormControl.Select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              data-testid="SEL_control_deficiency_severity_filter"
            >
              <option value="All">All Severities</option>
              {DEFICIENCY_SEVERITIES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </FormControl.Select>
            <FormControl.SearchSelect
              value={statusFilter}
              onChange={v => setStatusFilter(String(v ?? 'All'))}
              className="filter-status"
              data-testid="SEL_control_deficiency_status_filter"
              noSearchIcon
              options={[
                { value: 'All', label: 'All Statuses' },
                ...DEFICIENCY_STATUSES.map((s) => ({ value: s, label: s })),
              ]}
            />
            <FormControl.Input
              type="text"
              placeholder="Search deficiencies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_control_deficiency_search"
            />
            <Button
              variant="fill"
              icon={Plus}
              onClick={() => { setForm(BLANK_FORM); setShowCreateModal(true); }}
              disabled={permLoading || !canModify}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_control_deficiency_create"
            >
              Create
            </Button>
          </div>
        </div>

        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">S.No</th>
                      <th className="table-list-th">Control</th>
                      <th className="table-list-th">Deficiency</th>
                      <th className="table-list-th">Program</th>
                      <th className="table-list-th">Geography</th>
                      <th className="table-list-th">Detected</th>
                      <th className="table-list-th table-list-cell--priority">Severity</th>
                      <th className="table-list-th">Raised By</th>
                      <th className="table-list-th table-list-cell--status">Status</th>
                      <th className="table-list-th table-list-th--right">Actions</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {loading && <Skeleton.TableRows cols={['3xs', '2xs', 'md', 'sm', 'sm', '2xs', 'pill', 'sm', 'pill', { width: 'icon', align: 'right', count: 1 }]} />}
                    {!loading && deficiencies.length === 0 && (
                      <tr>
                        <td colSpan={10} className="table-list-cell">
                          <NoDataFound title="No Deficiencies Found" message='Deficiencies raised from control tests appear here.' />
                        </td>
                      </tr>
                    )}
                    {!loading && deficiencies.map((item, idx) => (
                      <tr key={item.id} className="table-list-row" data-testid={`BTN_control_deficiency_row_${item.id}`}>
                        <td className="table-list-cell table-list-cell--sno">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="table-list-cell">
                          {item.control_code && (
                            <span className="text-xs text-purple-600 bg-purple-50 px-1 rounded">
                              {item.control_code}
                            </span>
                          )}
                          {item.control_name && (
                            <span className="table-list-cell-sub">{item.control_name}</span>
                          )}
                        </td>
                        <td className="table-list-cell" title={item.description} style={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {item.title && <span className="table-list-cell-title">{item.title}</span>}
                          <span className="table-list-cell-sub">{item.description}</span>
                        </td>
                        <td className="table-list-cell">
                          {item.source_test_name || '—'}
                        </td>
                        <td className="table-list-cell">
                          {item.geography_name || '—'}
                        </td>
                        <td className="table-list-cell">{formatDate(item.detected_on)}</td>
                        <td className="table-list-cell">
                          <span className={severityBadgeCss(item.detected_severity)}>{item.detected_severity}</span>
                        </td>
                        <td className="table-list-cell">{item.created_by_name || item.owner_name || '—'}</td>
                        <td className="table-list-cell">
                          <span className={`inline-block whitespace-nowrap text-xs font-medium px-2 py-0.5 rounded-full ${deficiencyStageClass(item.status)}`}>{deficiencyStageLabel(item.status)}</span>
                        </td>
                        <td className="table-list-cell table-list-cell--actions">
                          {item.status !== "Closed" ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleClose(item); }}
                              className="table-list-action table-list-action--delete"
                              disabled={permLoading || !canModify}
                              title={!canModify ? NOT_AUTHORISED_MSG : "Close Deficiency"}
                              data-testid={`BTN_control_deficiency_row_${item.id}_close`}
                            >
                              <CheckCircle size={14} />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Closed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={total}
                onPageChange={setPage}
                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                recordLabel="deficiencies"
              />
            </div>
          </div>
        </div>

        {/* Create Deficiency Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          size="md"
          data-testid="MOD_control_deficiency_create"
        >
          <Modal.Header>Create Deficiency</Modal.Header>
          <Modal.Body>
            <form
              id="deficiency-form"
              onSubmit={handleCreateSubmit}
              className="grid grid-cols-2 gap-x-6 gap-y-4"
              data-testid="MOD_control_deficiency_form"
            >
              <ValidatedInput
                vh={vh}
                name="title"
                code={109}
                required
                label="Title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Describe the deficiency"
                data-testid="INP_control_deficiency_title"
              />
              <FormControl.SearchSelect
                label="Control"
                required
                options={controlOptions}
                value={form.controlId === "" ? null : form.controlId}
                onChange={(v) => setForm((f) => ({ ...f, controlId: v ? Number(v) : "" }))}
                placeholder="Select control"
                data-testid="SEL_control_deficiency_control"
              />
              <FormControl.SearchSelect
                label="Source"
                required
                options={SOURCE_OPTIONS}
                value={form.source}
                onChange={(value) => setForm((f) => ({ ...f, source: value as string }))}
                placeholder="Select source"
                data-testid="SEL_control_deficiency_source"
              />
              <FormControl.SearchSelect
                label="Severity"
                required
                options={SEVERITY_OPTIONS}
                value={form.severity}
                onChange={(value) => setForm((f) => ({ ...f, severity: value as string }))}
                placeholder="Select severity"
                data-testid="SEL_control_deficiency_severity"
              />
              <div className="col-span-2">
                <ValidatedTextarea
                  vh={vh}
                  name="description"
                  code={110}
                  required
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Detailed description of the deficiency…"
                  rows={3}
                  data-testid="INP_control_deficiency_description"
                />
              </div>
            </form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="submit"
              form="deficiency-form"
              variant="fill"
              icon={Save}
              disabled={permLoading || !canModify || submitting}
              title={!canModify ? NOT_AUTHORISED_MSG : undefined}
              data-testid="BTN_control_deficiency_submit"
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default ControlDeficiencies;
